import { useState, useCallback, useEffect } from 'react';
import { ReservationStep, ReservationData } from '../types/reservation.types';

const STORAGE_KEY = 'reservation_flow_data';

const initialData: ReservationData = {
  partySize: null,
  date: null,
  timeSlot: null,
  zonePreference: null,
  tableId: null,
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  notes: '',
  gdprConsent: false,
  turnstileToken: null,
};

const STEPS: ReservationStep[] = ['guests', 'date', 'time', 'zone', 'contact', 'success'];

export function useReservationFlow() {
  const [currentStep, setCurrentStep] = useState<ReservationStep>('guests');
  const [data, setData] = useState<ReservationData>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...initialData,
          ...parsed,
          date: parsed.date ? new Date(parsed.date) : null,
        };
      }
    } catch {}
    return initialData;
  });

  // Persist to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...data,
        date: data.date?.toISOString() ?? null,
      }));
    } catch {}
  }, [data]);

  const stepIndex = STEPS.indexOf(currentStep);

  const canProceed = useCallback((): boolean => {
    switch (currentStep) {
      case 'guests': return data.partySize !== null && data.partySize > 0;
      case 'date': return data.date !== null;
      case 'time': return data.timeSlot !== null;
      case 'zone': return true; // zone preference is optional
      case 'contact':
        return (
          data.guestName.trim().length > 0 &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.guestEmail) &&
          data.guestPhone.trim().length >= 6 &&
          data.gdprConsent
        );
      default: return false;
    }
  }, [currentStep, data]);

  const next = useCallback(() => {
    if (stepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[stepIndex + 1]);
    }
  }, [stepIndex]);

  const back = useCallback(() => {
    if (stepIndex > 0) {
      setCurrentStep(STEPS[stepIndex - 1]);
    }
  }, [stepIndex]);

  const skipZone = useCallback(() => {
    // Skip zone step, go directly to contact
    setCurrentStep('contact');
  }, []);

  const updateData = useCallback((updates: Partial<ReservationData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const reset = useCallback(() => {
    setData(initialData);
    setCurrentStep('guests');
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  const goToSuccess = useCallback(() => {
    setCurrentStep('success');
  }, []);

  return {
    currentStep,
    data,
    stepIndex,
    totalSteps: STEPS.length - 1, // exclude success from count
    canProceed: canProceed(),
    next,
    back,
    skipZone,
    updateData,
    reset,
    goToSuccess,
  };
}
